/**
 * The BCP-47 locale that matches the language the user picked in the app.
 *
 * Date formatting was split three ways before this: 17 sites hard-pinned
 * 'en-US', 22 passed `undefined` (which follows the BROWSER, not the app), and
 * dayjs had no locale registered at all. The result was "December 1, 2025" and
 * "Jul 28" chart ticks sitting in an otherwise Hebrew page — the single most
 * obvious tell that a UI was translated rather than localized.
 *
 * Kept as a plain module rather than a hook because most callers are helper
 * functions outside React (formatDate, tick formatters, table renderers) where
 * a hook cannot go. `useLanguage` keeps it in sync on every language change,
 * and components re-render on that same change, so the two stay consistent.
 */
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import 'dayjs/locale/en';

export type AppLanguage = 'en' | 'he';

const LOCALES: Record<AppLanguage, string> = {
  en: 'en-US',
  he: 'he-IL',
};

function readStored(): AppLanguage {
  try {
    return localStorage.getItem('language') === 'he' ? 'he' : 'en';
  } catch {
    return 'en';
  }
}

let current: AppLanguage = readStored();
dayjs.locale(current);

/** Call whenever the app language changes. */
export function setActiveLanguage(lang: AppLanguage): void {
  current = lang;
  dayjs.locale(lang);
}

/** BCP-47 tag for Intl / toLocaleDateString. */
export function activeLocale(): string {
  return LOCALES[current];
}

/** BCP-47 tag for a given language - a document that reads in its own language (an Insights report). */
export function localeFor(lang: AppLanguage): string {
  return LOCALES[lang];
}


/**
 * dayjs pattern strings, per language.
 *
 * A shared `MMM D, YYYY` is not language-neutral even once the dayjs
 * locale is registered: the MONTH NAME localizes, but the ORDER and the comma
 * do not. Hebrew writes "31 באוגוסט 2026", not "אוג׳ 31, 2026" — so a shared
 * pattern is as wrong as a shared English string, just less obviously.
 *
 * Kept here rather than in translation.json because a dayjs pattern is code,
 * not copy: a translator editing `MMM` into `ממ` would silently produce a
 * broken date, and these never need to differ per tenant.
 */
const DATE_PATTERNS: Record<AppLanguage, Record<string, string>> = {
  en: {
    monthDay: 'MMM D',
    monthDayYear: 'MMM D, YYYY',
    monthDayTime: 'MMM D, HH:mm',
  },
  he: {
    monthDay: 'D [ב]MMM',
    monthDayYear: 'D [ב]MMM YYYY',
    monthDayTime: 'D [ב]MMM, HH:mm',
  },
};

export type DatePatternKind = 'monthDay' | 'monthDayYear' | 'monthDayTime';

export function datePattern(kind: DatePatternKind): string {
  return DATE_PATTERNS[current][kind];
}
